import { defineMessages } from '../define';

/**
 * The sessions list and the session detail screen. Status wording is not here -
 * it lives in `status.*`, shared with the sidebar and every session picker.
 */
export const sessions = defineMessages({
  en: {
    'sessions.title': 'Sessions',
    'sessions.description': 'One LINE account per session, each with its own browser.',
    'sessions.connect': 'Connect a session',
    'sessions.searchPlaceholder': 'Search sessions…',
    'sessions.filterStatus': 'Filter by status',
    'sessions.filter.attention': 'Needs attention',
    'sessions.view': 'View',
    'sessions.view.grid': 'Grid view',
    'sessions.view.table': 'Table view',

    'sessions.empty.title': 'No sessions yet',
    'sessions.empty.description':
      'A session is one LINE account. Create one and scan the QR code with the LINE app on your phone.',
    'sessions.empty.filtered.title': 'No sessions match those filters',
    'sessions.empty.filtered.description':
      'Clear the search or pick a different status to see the rest.',

    'sessions.column.status': 'Status',
    'sessions.column.name': 'Name',
    'sessions.column.account': 'LINE account',
    'sessions.column.chats': 'Chats',
    'sessions.column.messages': 'Messages',
    'sessions.column.today': 'In / out today',
    'sessions.column.lastSeen': 'Last seen',
    'sessions.column.type': 'Type',
    'sessions.column.activity': 'Activity',

    'sessions.counter.chats': 'Chats',
    'sessions.counter.messages': 'Messages',
    'sessions.counter.inToday': 'In today',
    'sessions.counter.outToday': 'Out today',
    'sessions.counters': 'Counters',

    'sessions.notSignedIn': 'Not signed in',
    'sessions.signedIn': 'Signed in',
    'sessions.scanToConnect': 'Scan to connect',
    'sessions.expiresIn': 'Expires in',
    'sessions.actionsFor': 'Actions for {name}',
    'sessions.moreActions': 'More session actions',
    'sessions.open': 'Open',
    'sessions.restart': 'Restart',
    'sessions.logout': 'Log out',
    'sessions.screenshot': 'Screenshot',
    'sessions.backup': 'Back up profile',

    'sessions.restarting': 'Restarting {name}',
    'sessions.restarting.hint': 'Watch for the QR code.',
    'sessions.restartingBrowser': 'Restarting the browser',
    'sessions.restartFailed': 'Could not restart the session',
    'sessions.loggedOut': '{name} signed out of LINE',
    'sessions.signedOut': 'Signed out of LINE',
    'sessions.signedOut.hint': 'The next launch shows a new QR code.',
    'sessions.logoutFailed': 'Could not log the session out',
    'sessions.screenshotSaved': 'Screenshot saved',
    'sessions.screenshotFailed': 'Could not take a screenshot',
    'sessions.backedUp': 'Profile backed up',
    'sessions.backupFailed': 'Could not back up the profile',
    'sessions.deleted': 'Session deleted',
    'sessions.deleteFailed': 'Could not delete the session',
    'sessions.updated': 'Session updated',
    'sessions.saveFailed': 'Could not save the session',
    'sessions.gatewaySilent': 'The gateway did not respond.',

    'sessions.delete.title': 'Delete {name}?',
    'sessions.delete.description':
      'The browser stops, its Chrome profile is wiped and every message, chat and contact captured by this session is deleted with it. This cannot be undone.',
    'sessions.delete.action': 'Delete session',

    'sessions.create.title': 'Connect a session',
    'sessions.create.description':
      'A session is one LINE account, running in its own browser on the server.',
    'sessions.create.name': 'Name',
    'sessions.create.namePlaceholder': 'Support desk',
    'sessions.create.nameHint':
      'Only you see this. The LINE profile name is filled in after the scan.',
    'sessions.create.help':
      'When you create this, a browser starts on the server and opens LINE. Within a few seconds a QR code appears on the session page. Open LINE on your phone, scan it, and confirm the login there — messages start arriving straight away.',
    'sessions.create.submit': 'Create session',
    'sessions.starting': '{name} is starting',
    'sessions.createFailed': 'Could not create the session',

    'sessions.autoRestart': 'Auto restart',
    'sessions.autoRestart.hint': 'Bring the browser back up automatically if it crashes.',
    'sessions.keepAlive': 'Keep alive',
    'sessions.keepAlive.hint': 'Keep the browser running when nobody has the console open.',

    'sessions.tab.overview': 'Overview',
    'sessions.tab.messages': 'Messages',
    'sessions.tab.chats': 'Chats',
    'sessions.tab.logs': 'Logs',
    'sessions.tab.settings': 'Settings',

    'sessions.pin.title': 'Enter this number on your phone',
    'sessions.pin.description': 'LINE is asking your phone to confirm the login.',
    'sessions.pin.aria': 'Verification code {code}',
    'sessions.pin.step1': 'Your phone is showing a field for a verification number.',
    'sessions.pin.step2': 'Type the number on the left into it.',
    'sessions.pin.step3':
      'The session turns green on its own once LINE accepts it. If it lapses, restart the session and scan again.',

    'sessions.qr.title': 'Scan to sign in',
    'sessions.qr.description': 'The code refreshes on its own until someone scans it.',
    'sessions.qr.generating': 'Generating a code…',
    'sessions.qr.alt': 'LINE login QR code',
    'sessions.qr.expires': 'Expires',
    'sessions.qr.refreshed.one': 'Refreshed {count} time in this wait',
    'sessions.qr.refreshed.other': 'Refreshed {count} times in this wait',
    'sessions.qr.step1': 'Open LINE on the phone that owns this account.',
    'sessions.qr.step2': 'Go to Settings, then Move account or Log in with QR code.',
    'sessions.qr.step3': 'Point the camera at this code and confirm the login on the phone.',

    'sessions.lineAccount': 'LINE account',
    'sessions.copyMid': 'Copy LINE mid',
    'sessions.field.connected': 'Connected',
    'sessions.field.lastSeen': 'Last seen',
    'sessions.field.created': 'Created',
    'sessions.field.updated': 'Updated',

    'sessions.connection': 'Connection',
    'sessions.connection.hint':
      'Nothing to do here until the session settles. Restart it if it stays this way.',
    'sessions.error.title': 'The session stopped with an error',
    'sessions.error.noDetail': 'No detail was recorded.',

    'sessions.chrome.none': 'No browser is running for this session.',
    'sessions.chrome.notRunning': 'not running',
    'sessions.chrome.memory': 'Memory',
    'sessions.chrome.restarts': 'Restarts',
    'sessions.chrome.started': 'Started',
    'sessions.chrome.observer': 'Observer',
    'sessions.chrome.attached': 'attached',
    'sessions.chrome.starting': 'starting',
    'sessions.chrome.profile': 'Profile',

    'sessions.test.title': 'Send a test message',
    'sessions.test.description': 'Goes through the real browser, exactly as an operator reply would.',
    'sessions.test.target': 'Chat name or id',
    'sessions.test.targetPlaceholder': 'Sales team',
    'sessions.test.message': 'Message',
    'sessions.test.send': 'Send',
    'sessions.test.sent': 'Message sent',
    'sessions.test.refused': 'The extension refused the message',
    'sessions.test.failed': 'Could not send the message',
    'sessions.test.accepted': 'accepted',
    'sessions.test.rejected': 'rejected',

    'sessions.messages.title': 'Messages',
    'sessions.messages.description': 'Newest first, across every chat in this session',
    'sessions.messages.empty.title': 'No messages yet',
    'sessions.messages.empty.description':
      'Anything this account sends or receives lands here within a second of it happening.',
    'sessions.direction.inbound': 'Inbound',
    'sessions.direction.outbound': 'Outbound',

    'sessions.chats.empty.title': 'No chats yet',
    'sessions.chats.empty.description':
      'Conversations appear as soon as the extension sees them in the sidebar.',

    'sessions.logs.title': 'Logs',
    'sessions.logs.description': 'Newest first, streaming as they are written',
    'sessions.logs.empty.title': 'Nothing logged yet',
    'sessions.logs.empty.description': 'This session has not written a line.',

    'sessions.settings.name': 'Name',
    'sessions.settings.nameHint': 'Shown everywhere in the console. LINE never sees it.',
    'sessions.settings.nameLabel': 'Session name',
    'sessions.settings.behaviour': 'Behaviour',

    'sessions.danger': 'Danger zone',
    'sessions.danger.logout': 'Log out of LINE',
    'sessions.danger.logoutHint':
      'Wipes the Chrome profile. Messages already captured are kept; the next launch shows a new QR code.',
    'sessions.danger.delete': 'Delete this session',
    'sessions.danger.deleteHint':
      'Every message, chat and contact captured by this session is deleted with it.',

    'sessions.restore.action': 'Restore profile',
    'sessions.restore.title': 'Restore a profile',
    'sessions.restore.description':
      'The browser stops, the profile is replaced, and it comes back up if it was running.',
    'sessions.restore.backupId': 'Backup id',
    'sessions.restore.backupHint': 'Shown when the backup was taken, and recorded in the audit log.',
    'sessions.restore.submit': 'Restore',
    'sessions.restored': 'Profile restored',
    'sessions.restored.hint': 'The browser is coming back up.',
    'sessions.restoreFailed': 'Could not restore the profile',
  },
  th: {
    'sessions.title': 'เซสชัน',
    'sessions.description': 'หนึ่งเซสชันคือหนึ่งบัญชี LINE โดยแต่ละเซสชันมีเบราว์เซอร์ของตัวเอง',
    'sessions.connect': 'เชื่อมต่อเซสชัน',
    'sessions.searchPlaceholder': 'ค้นหาเซสชัน…',
    'sessions.filterStatus': 'กรองตามสถานะ',
    'sessions.filter.attention': 'ต้องตรวจสอบ',
    'sessions.view': 'มุมมอง',
    'sessions.view.grid': 'มุมมองการ์ด',
    'sessions.view.table': 'มุมมองตาราง',

    'sessions.empty.title': 'ยังไม่มีเซสชัน',
    'sessions.empty.description':
      'หนึ่งเซสชันคือหนึ่งบัญชี LINE สร้างเซสชันแล้วสแกน QR code ด้วยแอป LINE บนโทรศัพท์ของคุณ',
    'sessions.empty.filtered.title': 'ไม่มีเซสชันที่ตรงกับตัวกรองนี้',
    'sessions.empty.filtered.description': 'ล้างคำค้นหาหรือเลือกสถานะอื่นเพื่อดูรายการที่เหลือ',

    'sessions.column.status': 'สถานะ',
    'sessions.column.name': 'ชื่อ',
    'sessions.column.account': 'บัญชี LINE',
    'sessions.column.chats': 'แชท',
    'sessions.column.messages': 'ข้อความ',
    'sessions.column.today': 'เข้า / ออก วันนี้',
    'sessions.column.lastSeen': 'พบล่าสุด',
    'sessions.column.type': 'ประเภท',
    'sessions.column.activity': 'ความเคลื่อนไหว',

    'sessions.counter.chats': 'แชท',
    'sessions.counter.messages': 'ข้อความ',
    'sessions.counter.inToday': 'เข้าวันนี้',
    'sessions.counter.outToday': 'ออกวันนี้',
    'sessions.counters': 'ตัวนับ',

    'sessions.notSignedIn': 'ยังไม่ได้เข้าสู่ระบบ',
    'sessions.signedIn': 'เข้าสู่ระบบแล้ว',
    'sessions.scanToConnect': 'สแกนเพื่อเชื่อมต่อ',
    'sessions.expiresIn': 'หมดอายุใน',
    'sessions.actionsFor': 'การทำงานสำหรับ {name}',
    'sessions.moreActions': 'การทำงานอื่นของเซสชัน',
    'sessions.open': 'เปิด',
    'sessions.restart': 'เริ่มใหม่',
    'sessions.logout': 'ออกจากระบบ',
    'sessions.screenshot': 'ภาพหน้าจอ',
    'sessions.backup': 'สำรองโปรไฟล์',

    'sessions.restarting': 'กำลังเริ่ม {name} ใหม่',
    'sessions.restarting.hint': 'รอดู QR code ได้เลย',
    'sessions.restartingBrowser': 'กำลังเริ่มเบราว์เซอร์ใหม่',
    'sessions.restartFailed': 'เริ่มเซสชันใหม่ไม่สำเร็จ',
    'sessions.loggedOut': '{name} ออกจากระบบ LINE แล้ว',
    'sessions.signedOut': 'ออกจากระบบ LINE แล้ว',
    'sessions.signedOut.hint': 'ครั้งต่อไปที่เริ่มทำงานจะแสดง QR code ใหม่',
    'sessions.logoutFailed': 'ออกจากระบบเซสชันไม่สำเร็จ',
    'sessions.screenshotSaved': 'บันทึกภาพหน้าจอแล้ว',
    'sessions.screenshotFailed': 'ถ่ายภาพหน้าจอไม่สำเร็จ',
    'sessions.backedUp': 'สำรองโปรไฟล์แล้ว',
    'sessions.backupFailed': 'สำรองโปรไฟล์ไม่สำเร็จ',
    'sessions.deleted': 'ลบเซสชันแล้ว',
    'sessions.deleteFailed': 'ลบเซสชันไม่สำเร็จ',
    'sessions.updated': 'อัปเดตเซสชันแล้ว',
    'sessions.saveFailed': 'บันทึกเซสชันไม่สำเร็จ',
    'sessions.gatewaySilent': 'เกตเวย์ไม่ตอบสนอง',

    'sessions.delete.title': 'ลบ {name} หรือไม่?',
    'sessions.delete.description':
      'เบราว์เซอร์จะหยุดทำงาน โปรไฟล์ Chrome จะถูกล้าง และข้อความ แชท รวมถึงรายชื่อทั้งหมดที่เซสชันนี้เก็บไว้จะถูกลบไปด้วย การกระทำนี้ย้อนกลับไม่ได้',
    'sessions.delete.action': 'ลบเซสชัน',

    'sessions.create.title': 'เชื่อมต่อเซสชัน',
    'sessions.create.description': 'หนึ่งเซสชันคือหนึ่งบัญชี LINE ที่ทำงานในเบราว์เซอร์ของตัวเองบนเซิร์ฟเวอร์',
    'sessions.create.name': 'ชื่อ',
    'sessions.create.namePlaceholder': 'ฝ่ายดูแลลูกค้า',
    'sessions.create.nameHint': 'มีเพียงคุณที่เห็นชื่อนี้ ชื่อโปรไฟล์ LINE จะถูกเติมให้หลังสแกนเสร็จ',
    'sessions.create.help':
      'เมื่อสร้างแล้ว เบราว์เซอร์จะเริ่มทำงานบนเซิร์ฟเวอร์และเปิด LINE ภายในไม่กี่วินาที QR code จะปรากฏบนหน้าเซสชัน เปิด LINE บนโทรศัพท์ สแกน แล้วยืนยันการเข้าสู่ระบบบนโทรศัพท์ — จากนั้นข้อความจะเข้ามาทันที',
    'sessions.create.submit': 'สร้างเซสชัน',
    'sessions.starting': '{name} กำลังเริ่มทำงาน',
    'sessions.createFailed': 'สร้างเซสชันไม่สำเร็จ',

    'sessions.autoRestart': 'เริ่มใหม่อัตโนมัติ',
    'sessions.autoRestart.hint': 'เปิดเบราว์เซอร์ขึ้นมาใหม่ให้อัตโนมัติหากเกิดข้อขัดข้อง',
    'sessions.keepAlive': 'ทำงานต่อเนื่อง',
    'sessions.keepAlive.hint': 'ให้เบราว์เซอร์ทำงานต่อแม้ไม่มีใครเปิดคอนโซลอยู่',

    'sessions.tab.overview': 'ภาพรวม',
    'sessions.tab.messages': 'ข้อความ',
    'sessions.tab.chats': 'แชท',
    'sessions.tab.logs': 'บันทึกระบบ',
    'sessions.tab.settings': 'ตั้งค่า',

    'sessions.pin.title': 'กรอกตัวเลขนี้บนโทรศัพท์ของคุณ',
    'sessions.pin.description': 'LINE กำลังขอให้ยืนยันการเข้าสู่ระบบจากโทรศัพท์ของคุณ',
    'sessions.pin.aria': 'รหัสยืนยัน {code}',
    'sessions.pin.step1': 'โทรศัพท์ของคุณกำลังแสดงช่องให้กรอกรหัสยืนยัน',
    'sessions.pin.step2': 'กรอกตัวเลขทางซ้ายลงในช่องนั้น',
    'sessions.pin.step3':
      'เซสชันจะเปลี่ยนเป็นสีเขียวเองเมื่อ LINE ยอมรับรหัส หากรหัสหมดเวลา ให้เริ่มเซสชันใหม่แล้วสแกนอีกครั้ง',

    'sessions.qr.title': 'สแกนเพื่อเข้าสู่ระบบ',
    'sessions.qr.description': 'รหัสจะรีเฟรชเองจนกว่าจะมีคนสแกน',
    'sessions.qr.generating': 'กำลังสร้างรหัส…',
    'sessions.qr.alt': 'QR code สำหรับเข้าสู่ระบบ LINE',
    'sessions.qr.expires': 'หมดอายุ',
    'sessions.qr.refreshed.one': 'รีเฟรชไปแล้ว {count} ครั้งในการรอรอบนี้',
    'sessions.qr.refreshed.other': 'รีเฟรชไปแล้ว {count} ครั้งในการรอรอบนี้',
    'sessions.qr.step1': 'เปิด LINE บนโทรศัพท์ที่เป็นเจ้าของบัญชีนี้',
    'sessions.qr.step2': 'ไปที่ตั้งค่า แล้วเลือกย้ายบัญชี หรือเข้าสู่ระบบด้วย QR code',
    'sessions.qr.step3': 'หันกล้องไปที่รหัสนี้ แล้วยืนยันการเข้าสู่ระบบบนโทรศัพท์',

    'sessions.lineAccount': 'บัญชี LINE',
    'sessions.copyMid': 'คัดลอก LINE mid',
    'sessions.field.connected': 'เชื่อมต่อเมื่อ',
    'sessions.field.lastSeen': 'พบล่าสุด',
    'sessions.field.created': 'สร้างเมื่อ',
    'sessions.field.updated': 'อัปเดตเมื่อ',

    'sessions.connection': 'การเชื่อมต่อ',
    'sessions.connection.hint':
      'ยังไม่ต้องทำอะไรจนกว่าเซสชันจะเข้าที่ หากค้างอยู่แบบนี้นาน ให้เริ่มเซสชันใหม่',
    'sessions.error.title': 'เซสชันหยุดทำงานเพราะข้อผิดพลาด',
    'sessions.error.noDetail': 'ไม่มีรายละเอียดถูกบันทึกไว้',

    'sessions.chrome.none': 'ไม่มีเบราว์เซอร์ทำงานอยู่สำหรับเซสชันนี้',
    'sessions.chrome.notRunning': 'ไม่ได้ทำงาน',
    'sessions.chrome.memory': 'หน่วยความจำ',
    'sessions.chrome.restarts': 'จำนวนครั้งที่เริ่มใหม่',
    'sessions.chrome.started': 'เริ่มเมื่อ',
    'sessions.chrome.observer': 'ตัวเฝ้าดู',
    'sessions.chrome.attached': 'เชื่อมต่อแล้ว',
    'sessions.chrome.starting': 'กำลังเริ่ม',
    'sessions.chrome.profile': 'โปรไฟล์',

    'sessions.test.title': 'ส่งข้อความทดสอบ',
    'sessions.test.description': 'ส่งผ่านเบราว์เซอร์จริง เหมือนกับตอนที่ผู้ปฏิบัติงานตอบเอง',
    'sessions.test.target': 'ชื่อแชทหรือ id',
    'sessions.test.targetPlaceholder': 'ทีมขาย',
    'sessions.test.message': 'ข้อความ',
    'sessions.test.send': 'ส่ง',
    'sessions.test.sent': 'ส่งข้อความแล้ว',
    'sessions.test.refused': 'ส่วนขยายปฏิเสธข้อความนี้',
    'sessions.test.failed': 'ส่งข้อความไม่สำเร็จ',
    'sessions.test.accepted': 'รับแล้ว',
    'sessions.test.rejected': 'ถูกปฏิเสธ',

    'sessions.messages.title': 'ข้อความ',
    'sessions.messages.description': 'เรียงจากใหม่สุด รวมทุกแชทในเซสชันนี้',
    'sessions.messages.empty.title': 'ยังไม่มีข้อความ',
    'sessions.messages.empty.description':
      'ทุกข้อความที่บัญชีนี้ส่งหรือได้รับจะมาอยู่ที่นี่ภายในเวลาไม่ถึงวินาที',
    'sessions.direction.inbound': 'ขาเข้า',
    'sessions.direction.outbound': 'ขาออก',

    'sessions.chats.empty.title': 'ยังไม่มีแชท',
    'sessions.chats.empty.description': 'บทสนทนาจะปรากฏทันทีที่ส่วนขยายเห็นในแถบด้านข้าง',

    'sessions.logs.title': 'บันทึกระบบ',
    'sessions.logs.description': 'เรียงจากใหม่สุด อัปเดตทันทีที่มีการบันทึก',
    'sessions.logs.empty.title': 'ยังไม่มีบันทึก',
    'sessions.logs.empty.description': 'เซสชันนี้ยังไม่ได้เขียนบันทึกใดเลย',

    'sessions.settings.name': 'ชื่อ',
    'sessions.settings.nameHint': 'แสดงทุกที่ในคอนโซล LINE ไม่เห็นชื่อนี้',
    'sessions.settings.nameLabel': 'ชื่อเซสชัน',
    'sessions.settings.behaviour': 'พฤติกรรมการทำงาน',

    'sessions.danger': 'โซนอันตราย',
    'sessions.danger.logout': 'ออกจากระบบ LINE',
    'sessions.danger.logoutHint':
      'ล้างโปรไฟล์ Chrome ข้อความที่เก็บไว้แล้วจะยังอยู่ และครั้งต่อไปที่เริ่มทำงานจะแสดง QR code ใหม่',
    'sessions.danger.delete': 'ลบเซสชันนี้',
    'sessions.danger.deleteHint': 'ข้อความ แชท และรายชื่อทั้งหมดที่เซสชันนี้เก็บไว้จะถูกลบไปด้วย',

    'sessions.restore.action': 'กู้คืนโปรไฟล์',
    'sessions.restore.title': 'กู้คืนโปรไฟล์',
    'sessions.restore.description':
      'เบราว์เซอร์จะหยุดทำงาน โปรไฟล์จะถูกแทนที่ แล้วจะกลับมาทำงานอีกครั้งหากก่อนหน้านี้เปิดอยู่',
    'sessions.restore.backupId': 'Backup id',
    'sessions.restore.backupHint': 'แสดงตอนที่สำรองข้อมูล และบันทึกไว้ในประวัติการใช้งาน',
    'sessions.restore.submit': 'กู้คืน',
    'sessions.restored': 'กู้คืนโปรไฟล์แล้ว',
    'sessions.restored.hint': 'เบราว์เซอร์กำลังกลับมาทำงาน',
    'sessions.restoreFailed': 'กู้คืนโปรไฟล์ไม่สำเร็จ',
  },
});
